import React from 'react';
import { ClipboardPaste, FileSpreadsheet, FileUp, PenLine, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { useTheme } from '../../../context/ThemeContext';
import {
  FUNDING_MODAL_OVERLAY_CLASS,
  getFundGeniusPortalAttrs,
} from './fundingDrawerUi';

const CHOICES = [
  {
    id: 'manual',
    icon: PenLine,
    title: 'Start Manual Intake',
    description: 'Enter one client, identity, business, income, address, credit profile, and funding goal manually.',
    bullets: ['Full field intake', 'SSN and EIN protected', 'Save draft or create client'],
    action: 'Start manual intake',
  },
  {
    id: 'paste',
    icon: ClipboardPaste,
    title: 'Paste Client Info',
    description: 'Paste client details, email notes, copied application info, or OCR text. FundGenius extracts fields so you can review before creating the client.',
    bullets: ['Paste from email, notes, SMS, or OCR', 'Extract identity, income, business, and tax fields', 'Review before saving'],
    action: 'Paste client info',
  },
  {
    id: 'upload',
    icon: FileUp,
    title: 'Upload Documents',
    description: 'Upload ID, tax returns, bank statements, EIN letters, paystubs, or business documents. FundGenius extracts fields and lets you review before saving.',
    bullets: ['Classify documents', 'Extract fields with OCR', 'Save to client vault'],
    action: 'Upload documents',
  },
  {
    id: 'bulk',
    icon: FileSpreadsheet,
    title: 'Import Client List',
    description: 'Upload CSV or Excel to import multiple clients, map columns, validate rows, and import clean records.',
    bullets: ['CSV, XLS, XLSX', 'Column mapping', 'Error review before import'],
    action: 'Import client list',
  },
];

const NewFundingClientModal = ({ open, onOpenChange, onSelectOption }) => {
  const { resolvedMode } = useTheme();
  const portalAttrs = getFundGeniusPortalAttrs(resolvedMode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName={FUNDING_MODAL_OVERLAY_CLASS}
        className="funding-client-modal fg-new-client-modal max-h-[90vh] w-full max-w-[1020px] overflow-y-auto"
        data-testid="new-funding-client-modal"
        hideClose
        {...portalAttrs}
      >
        <DialogHeader>
          <DialogTitle>Create Funding Client</DialogTitle>
          <DialogDescription>Choose how you want to add clients to your funding pipeline.</DialogDescription>
        </DialogHeader>

        <div className="funding-create-client-options">
          {CHOICES.map((choice) => (
            <button
              key={choice.id}
              type="button"
              className="funding-create-client-option"
              data-testid={`new-client-option-${choice.id}`}
              onClick={() => onSelectOption(choice.id)}
            >
              <div>
                <div className="funding-create-client-option__icon">
                  <choice.icon className="size-5" aria-hidden="true" />
                </div>
                <h3>{choice.title}</h3>
                <p>{choice.description}</p>
                <ul>
                  {choice.bullets.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <span className="funding-create-client-option__cta">{choice.action}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          className="absolute right-4 top-4 rounded-lg p-1 text-[var(--funding-text-secondary)] hover:text-[var(--funding-text-primary)]"
          onClick={() => onOpenChange(false)}
          aria-label="Close"
        >
          <X className="size-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default NewFundingClientModal;
